import React, { useState, useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import { isEmpty, isArray, head, get } from 'lodash';
import { useGranularEffect } from 'granular-hooks';
import { Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';

function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}

var _excluded$1 = ["inputProps"];

/**
 * This helper returns a function for defining a renderInput prop on a MUI Autocomplete
 * @param {object} props
 * @prop {object} inputProps
 * @returns function
 */
var getRenderInput = function getRenderInput(_ref) {
  var inputProps = _ref.inputProps,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var RenderInput = function RenderInput(args) {
    return /*#__PURE__*/React.createElement(TextField, _extends({
      variant: "outlined"
    }, args, rest, {
      inputProps: _extends({
        autoComplete: "off"
      }, args.inputProps, inputProps)
    }));
  };
  return RenderInput;
};

/**
 * This fn returns a loading state that gets updated when the returned search fn is used
 * @param {func} onSearch
 * @returns object with loading state & search fn
 */
var useLoadingSearch = function useLoadingSearch(onSearch) {
  var _useState = useState(false),
    loading = _useState[0],
    setLoading = _useState[1];
  var search = useCallback(function (value) {
    setLoading(true);
    return onSearch(value)["finally"](function () {
      setLoading(false);
    });
  }, [onSearch]);
  return {
    loading: loading,
    search: search
  };
};

var _excluded = ["clearOnSelect", "helperText", "inputProps", "label", "labelKey", "onSearch", "onChange", "placeholder", "showLoading", "value"];

/**
 * Configures an Autocomplete from MUI with things we often need
 * Adds functionality for being able to clear the field from a parent changing the value
 * Adds clearOnSelect for clearing the text input when a selection is made
 * Adds the functionality to take on the text of the label into the input when a selection is made and clearOnSelect is not turned on.
 * @param {object} props
 * @prop {bool} clearOnSelect
 * @prop {string} helperText
 * @prop {string} label
 * @prop {string} placeholder
 * @prop {string} labelKey
 * @prop {bool} inputProps
 * @prop {bool} showLoading
 * @prop {value} showLoading
 * @returns ControlledAutocomplete component
 */
var ControlledAutocomplete = function ControlledAutocomplete(_ref) {
  var _ref$clearOnSelect = _ref.clearOnSelect,
    clearOnSelect = _ref$clearOnSelect === void 0 ? false : _ref$clearOnSelect,
    helperText = _ref.helperText;
    _ref.inputProps;
    var label = _ref.label,
    _ref$labelKey = _ref.labelKey,
    labelKey = _ref$labelKey === void 0 ? 'name' : _ref$labelKey,
    onSearch = _ref.onSearch,
    onChange = _ref.onChange,
    placeholder = _ref.placeholder,
    showLoading = _ref.showLoading,
    value = _ref.value,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var key = useRef(0);
  var _useState = useState([]),
    options = _useState[0],
    setOptions = _useState[1];
  var getOptionLabel = function getOptionLabel(value) {
    var v = isArray(value) ? head(value) : value;
    return get(v, labelKey, v || '');
  };
  var initValue = useRef(getOptionLabel(value) || '');
  var _useState2 = useState(initValue.current),
    inputValue = _useState2[0],
    setInputValue = _useState2[1];
  var _useLoadingSearch = useLoadingSearch(onSearch),
    loading = _useLoadingSearch.loading,
    search = _useLoadingSearch.search;
  var onInputChange = useCallback(function (e, value, type) {
    var isInputting = type === 'input';
    var isResetting = type === 'reset';
    var isClearing = !isInputting || isEmpty(value);
    var inputValue = isClearing || !value ? '' : value;
    if (isResetting && clearOnSelect || !isResetting) {
      setInputValue(inputValue);
    }
  }, [clearOnSelect]);
  var onSelect = useCallback(function (e, value) {
    if (clearOnSelect) {
      setInputValue('');
    } else {
      var textValue = getOptionLabel(value);
      setInputValue(textValue);
    }
    onChange(value);
  }, [clearOnSelect, onChange, labelKey]);
  var closeIcon = useMemo(function () {
    if (clearOnSelect && !isEmpty(value)) {
      return '';
    }
  }, [value, clearOnSelect]);
  useGranularEffect(function () {
    var unmounted = false;
    var hasInput = !isEmpty(inputValue);
    var hasOptions = !isEmpty(options);
    var shouldClear = !hasInput && hasOptions;
    if (shouldClear) {
      setOptions([]);
    } else if (hasInput) {
      search(inputValue).then(function (options) {
        if (!unmounted) setOptions(options);
      });
    }
    return function () {
      unmounted = true;
    };
  }, [inputValue], [search]);
  useGranularEffect(function () {
    if (isEmpty(value) && !isEmpty(inputValue)) {
      setInputValue('');
    }
  }, [value], [inputValue]);
  return /*#__PURE__*/React.createElement(Autocomplete, _extends({
    closeIcon: closeIcon,
    inputValue: inputValue,
    key: key.current,
    loading: !!showLoading && loading,
    getOptionLabel: getOptionLabel,
    options: options,
    onChange: onSelect,
    onInputChange: onInputChange,
    renderInput: getRenderInput({
      label: label,
      helperText: helperText,
      autoFocus: key.current > 0
    }),
    label: label || placeholder,
    placeholder: placeholder,
    value: value
  }, rest));
};
ControlledAutocomplete.propTypes = {
  onSearch: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string,
  inputProps: PropTypes.object,
  labelKey: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.array.isRequired, PropTypes.string.isRequired, PropTypes.object.isRequired]),
  clearOnSelect: PropTypes.bool,
  showLoading: PropTypes.bool,
  placeholder: PropTypes.string,
  helperText: PropTypes.node
};

export { ControlledAutocomplete, getRenderInput, useLoadingSearch };
//# sourceMappingURL=index.module.js.map
